import { Tile, TerrainType, City, CampaignType, CityLevel } from './types';
import normandyMapData from './maps/normandy.csv?raw';
import tunisiaMapData from './maps/tunisia.csv?raw';
import stalingradMapData from './maps/stalingrad.csv?raw';
import okinawaMapData from './maps/okinawa.csv?raw';
import campobassoMapData from './maps/campobasso.csv?raw';
import tutorialMapData from './maps/tutorial.csv?raw';
import battleOfFranceMapData from './maps/battle-of-france.csv?raw';
import albaniaMapData from './maps/albania-1940.csv?raw';
import normandyCitiesData from './maps/cities.json';
import tunisiaCitiesData from './maps/tunisia-cities.json';
import stalingradCitiesData from './maps/stalingrad-cities.json';
import okinawaCitiesData from './maps/okinawa-cities.json';
import campobassoCitiesData from './maps/campobasso-cities.json';
import tutorialCitiesData from './maps/tutorial-cities.json';
import battleOfFranceCitiesData from './maps/battle-of-france-cities.json';
import albaniaCitiesData from './maps/albania-1940-cities.json';

export class GameMap {
  private tiles: Tile[][];
  private width: number;
  private height: number;
  private cities: Map<string, City> = new Map();
  private campaign: CampaignType;

  constructor(campaign: CampaignType = CampaignType.NORMANDY) {
    this.campaign = campaign;
    // Debug: Check if enum is properly loaded
    console.log('TerrainType enum object:', TerrainType);
    console.log('All terrain enum keys:', Object.keys(TerrainType));
    console.log('All terrain enum values:', Object.values(TerrainType));
    if (campaign === CampaignType.TUTORIAL) {
      this.width = 20;
      this.height = 20;
    } else {
      this.width = 60;
      this.height = 60;
    }
    this.tiles = this.loadMapFromCSV();
    this.loadCities();
  }

  private loadMapFromCSV(): Tile[][] {
    const map: Tile[][] = [];
    let mapData: string;
    switch (this.campaign) {
      case CampaignType.NORMANDY:
        mapData = normandyMapData;
        break;
      case CampaignType.TUNISIA:
        mapData = tunisiaMapData;
        break;
      case CampaignType.STALINGRAD:
        mapData = stalingradMapData;
        break;
      case CampaignType.OKINAWA:
        mapData = okinawaMapData;
        break;
      case CampaignType.CAMPOBASSO:
        mapData = campobassoMapData;
        break;
      case CampaignType.TUTORIAL:
        mapData = tutorialMapData;
        break;
      case CampaignType.FRANCE_1940:
        mapData = battleOfFranceMapData;
        break;
      case CampaignType.ALBANIA_1940:
        mapData = albaniaMapData;
        break;
      default:
        mapData = normandyMapData;
    }
    const lines = mapData.trim().split('\n');
    
    // Skip comment lines
    const dataLines = lines.filter(line => !line.startsWith('#'));
    
    for (let y = 0; y < this.height; y++) {
      const row: Tile[] = [];
      const rowData = dataLines[y]?.split(',') || [];
      
      for (let x = 0; x < this.width; x++) {
        const terrainCode = rowData[x]?.trim() || 'P';
        const terrain = this.getTerrainFromCode(terrainCode);
        
        row.push({
          x,
          y,
          terrain
        });
      }
      map.push(row);
    }
    
    return map;
  }
  
  private getTerrainFromCode(code: string): TerrainType {
    switch (this.campaign) {
      case CampaignType.NORMANDY:
        switch (code) {
          case 'E': return TerrainType.OPEN_FIELDS;      // Easy terrain
          case 'R': return TerrainType.BOCAGE;           // Rough terrain
          case 'C': return TerrainType.DENSE_WOODS;      // Challenging terrain
          case 'U': return TerrainType.URBAN;
          default: return TerrainType.OPEN_FIELDS;
        }
      
      case CampaignType.TUNISIA:
        switch (code) {
          case 'E': return TerrainType.DESERT_PLAINS;    // Easy terrain
          case 'R': return TerrainType.ROCKY_HIGHLANDS;  // Rough terrain
          case 'C': return TerrainType.SHIFTING_DUNES;   // Challenging terrain
          case 'U': return TerrainType.URBAN;
          default: return TerrainType.DESERT_PLAINS;
        }
      
      case CampaignType.STALINGRAD:
        switch (code) {
          case 'E': return TerrainType.FROZEN_STEPPE;    // Easy terrain
          case 'R': return TerrainType.SNOW_DRIFTS;      // Rough terrain
          case 'C': return TerrainType.FROZEN_MARSHES;   // Challenging terrain
          case 'U': return TerrainType.URBAN;
          default: return TerrainType.FROZEN_STEPPE;
        }
      
      case CampaignType.OKINAWA:
        switch (code) {
          case 'E': return TerrainType.RICE_PADDIES;     // Easy terrain
          case 'R': return TerrainType.CORAL_RIDGES;     // Rough terrain
          case 'C': return TerrainType.DENSE_JUNGLE;     // Challenging terrain
          case 'U': return TerrainType.URBAN;
          default: return TerrainType.RICE_PADDIES;
        }
      
      case CampaignType.CAMPOBASSO:
        switch (code) {
          case 'E': return TerrainType.VALLEY_FLOORS;    // Easy terrain
          case 'R': return TerrainType.WOODED_HILLS;     // Rough terrain
          case 'C': return TerrainType.MOUNTAIN_RIDGES;  // Challenging terrain
          case 'U': return TerrainType.URBAN;
          default: return TerrainType.VALLEY_FLOORS;
        }
      
      case CampaignType.TUTORIAL:
        switch (code) {
          case 'E': return TerrainType.OPEN_FIELDS;      // Easy terrain
          case 'R': return TerrainType.BOCAGE;           // Rough terrain
          case 'C': return TerrainType.DENSE_WOODS;      // Challenging terrain
          case 'U': return TerrainType.URBAN;
          default: return TerrainType.OPEN_FIELDS;
        }
      
      case CampaignType.FRANCE_1940:
        switch (code) {
          case 'E': return TerrainType.FRENCH_COUNTRYSIDE;  // Easy terrain - French countryside
          case 'R': return TerrainType.ROLLING_HILLS;       // Rough terrain - Rolling hills
          case 'C': return TerrainType.DENSE_FORESTS;       // Challenging terrain - Dense forests
          case 'U': return TerrainType.URBAN;
          case 'P': return TerrainType.FRENCH_COUNTRYSIDE;  // Fallback terrain
          default: return TerrainType.FRENCH_COUNTRYSIDE;
        }
      
      case CampaignType.ALBANIA_1940:
        switch (code) {
          case 'E': return TerrainType.COASTAL_LOWLANDS;    // Easy terrain - Coastal lowlands
          case 'R': return TerrainType.ALBANIAN_HIGHLANDS;  // Rough terrain - Albanian highlands
          case 'C': return TerrainType.PINDUS_MOUNTAINS;    // Challenging terrain - Pindus mountains
          case 'U': return TerrainType.URBAN;
          case 'P': return TerrainType.COASTAL_LOWLANDS;    // Fallback terrain
          default: return TerrainType.COASTAL_LOWLANDS;
        }
      
      default:
        return TerrainType.OPEN_FIELDS;
    }
  }

  private loadCities(): void {
    let citiesData: any;
    switch (this.campaign) {
      case CampaignType.NORMANDY:
        citiesData = normandyCitiesData;
        break;
      case CampaignType.TUNISIA:
        citiesData = tunisiaCitiesData;
        break;
      case CampaignType.STALINGRAD:
        citiesData = stalingradCitiesData;
        break;
      case CampaignType.OKINAWA:
        citiesData = okinawaCitiesData;
        break;
      case CampaignType.CAMPOBASSO:
        citiesData = campobassoCitiesData;
        break;
      case CampaignType.TUTORIAL:
        citiesData = tutorialCitiesData;
        break;
      case CampaignType.FRANCE_1940:
        citiesData = battleOfFranceCitiesData;
        break;
      case CampaignType.ALBANIA_1940:
        citiesData = albaniaCitiesData;
        break;
      default:
        citiesData = normandyCitiesData;
    }
    for (const cityData of citiesData.cities) {
      const city: City = {
        name: cityData.name,
        faction: null,
        tiles: cityData.tiles,
        unitsProduced: [],
        maxUnits: 3, // Level 1 cities start with 3 unit capacity
        level: CityLevel.OUTPOST // All cities start as Outposts
      };
      
      // Assign starting cities to factions (only if not already set from JSON)
      if (city.faction === null && this.campaign === CampaignType.NORMANDY) {
        if (cityData.name === 'Bayeux') {
          city.faction = '3rd Reich'; // Germans start with Bayeux (central position)
        } else if (cityData.name === 'Coutances') {
          city.faction = 'United States Of America'; // Americans start with Coutances (west coast)
        }
      } else if (city.faction === null && this.campaign === CampaignType.TUNISIA) {
        if (cityData.name === 'Gabès') {
          city.faction = '3rd Reich'; // Afrika Korps starts with Gabès (eastern defensive position)
        } else if (cityData.name === 'Tébessa') {
          city.faction = 'United Kingdom'; // British 8th Army starts with Tébessa (western approach)
        }
      } else if (city.faction === null && this.campaign === CampaignType.STALINGRAD) {
        if (cityData.name === 'Volkhov') {
          city.faction = 'USSR'; // Soviets start with Volkhov (southern position)
        } else if (cityData.name === 'Gumrak') {
          city.faction = '3rd Reich'; // Germans start with Gumrak (northern position)
        }
      } else if (city.faction === null && this.campaign === CampaignType.OKINAWA) {
        if (cityData.name === 'Shuri Castle') {
          city.faction = 'Imperial Japan'; // Japanese start with Shuri Castle (defensive position)
        } else if (cityData.name === 'Hagushi Beaches') {
          city.faction = 'United States Of America'; // US Marines start with Hagushi Beaches (landing zone)
        }
      } else if (city.faction === null && this.campaign === CampaignType.CAMPOBASSO) {
        if (cityData.name === 'Campobasso') {
          city.faction = 'Fascist Italy'; // Italians start with Campobasso (central stronghold)
        } else if (cityData.name === 'Vinchiaturo') {
          city.faction = 'Canada'; // Canadians start with Vinchiaturo (southern approach)
        }
      } else if (city.faction === null && this.campaign === CampaignType.TUTORIAL) {
        if (cityData.name === 'Tutorial Base') {
          city.faction = 'United States Of America'; // Player starts with Tutorial Base
        }
        // All other cities remain neutral in tutorial mode - Enemy Stronghold will be used later but starts neutral
        // This prevents AI from having any cities to work with
      } else if (city.faction === null && this.campaign === CampaignType.FRANCE_1940) {
        if (cityData.name === 'Paris') {
          city.faction = 'France'; // France starts with Paris (capital)
        } else if (cityData.name === 'Charleville') {
          city.faction = '3rd Reich'; // Germans start with Charleville (Ardennes staging point)
        }
      } else if (city.faction === null && this.campaign === CampaignType.ALBANIA_1940) {
        if (cityData.name === 'Tirana') {
          city.faction = 'Fascist Italy'; // Italy starts with Tirana (Albanian capital)
        } else if (cityData.name === 'Ioannina') {
          city.faction = 'Greece'; // Greece starts with Ioannina (northern Greece)
        }
      }
      
      this.cities.set(cityData.name, city);
      
      // Update tiles to be urban and reference the city
      for (const pos of cityData.tiles) {
        if (pos.x >= 0 && pos.x < this.width && pos.y >= 0 && pos.y < this.height) {
          const tile = this.tiles[pos.y][pos.x];
          tile.terrain = TerrainType.URBAN;
          tile.city = city;
        }
      }
    }
  }

  getTile(x: number, y: number): Tile | null {
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) {
      return null;
    }
    return this.tiles[y][x];
  }

  getTiles(): Tile[][] {
    return this.tiles;
  }
  
  getCities(): Map<string, City> {
    return this.cities;
  }

  getWidth(): number {
    return this.width;
  }

  getHeight(): number {
    return this.height;
  }
}